import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Truck, Package, CheckCircle2, ClipboardList, Plus, Trash2, AlertCircle, Shield, User, MapPin, Home } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NATURE_OPTIONS = [
  { value: "Purchase-RMT/FNG", label: "Purchase - RMT/FNG (Raw Material / Finished Goods)" },
  { value: "Purchase-CTM", label: "Purchase - CTM (Consumables / Tools / Machinery)" },
  { value: "Import Purchase", label: "Import Purchase (International / BOE Based)" },
  { value: "Jobwork-Own", label: "Jobwork - Own (Our material returning from jobber)" },
  { value: "Jobwork-Client", label: "Jobwork - Client (Client's material for our jobwork)" },
  { value: "Sales Return", label: "Sales Return (Goods returned by customer)" },
  { value: "Samples Returned", label: "Samples Returned (Our samples coming back)" },
  { value: "Samples Received", label: "Samples Received (New samples from others)" }
];

// Common UOM options for gate entry (simplified list for security personnel)
const UOM_OPTIONS = [
  { value: "NOS", label: "NOS (Numbers)" },
  { value: "KGS", label: "KGS (Kilograms)" },
  { value: "PCS", label: "PCS (Pieces)" },
  { value: "MTR", label: "MTR (Meters)" },
  { value: "LTR", label: "LTR (Liters)" },
  { value: "SET", label: "SET (Sets)" },
  { value: "BOX", label: "BOX (Boxes)" },
  { value: "PKT", label: "PKT (Packets)" },
  { value: "ROLL", label: "ROLL (Rolls)" },
  { value: "SQFT", label: "SQFT (Square Feet)" }
];

// Auto-caps input handler
const toUpperCase = (value) => value ? value.toUpperCase() : '';

// Get device ID from localStorage
const getDeviceId = () => {
  return localStorage.getItem('security_device_id') || '';
};

export default function GateEntryPublicPage() {
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [activeCheckin, setActiveCheckin] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [gateEntryNo, setGateEntryNo] = useState('');
  
  const [formData, setFormData] = useState({
    supplier_name_raw: '',
    invoice_dc_no: '',
    nature_of_inward: '',
    vehicle_no: '',
    driver_name: '',
    driver_contact: '',
    remarks: ''
  });
  
  const [items, setItems] = useState([
    { item_name: '', quantity: '', uom: 'NOS', num_packages: '' }
  ]);

  // Security check-in enforcement (set to false for production)
  const BYPASS_CHECKIN = false;

  // Check for valid security check-in
  useEffect(() => {
    const validateSession = async () => {
      // BYPASS MODE: Skip validation and use dummy checkin data
      if (BYPASS_CHECKIN) {
        setActiveCheckin({
          id: 'bypass-testing',
          security_person_id: 'bypass-testing',
          security_person_name: 'TEST SECURITY',
          unit_location_id: 'bypass-testing',
          unit_location_name: 'TEST LOCATION',
          device_id: 'bypass-testing'
        });
        setCheckingSession(false);
        return;
      }

      const deviceId = getDeviceId();
      
      if (!deviceId) {
        setCheckingSession(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/store/security-checkin/validate?device_id=${deviceId}`);
        if (response.data.valid) {
          setActiveCheckin(response.data.checkin);
        }
      } catch (error) {
        console.error('Error validating session:', error);
      }
      setCheckingSession(false);
    };
    
    validateSession();
  }, []);

  // Add new item row
  const addItem = () => {
    setItems([...items, { item_name: '', quantity: '', uom: 'NOS', num_packages: '' }]);
  };

  // Remove item row
  const removeItem = (index) => {
    if (items.length === 1) {
      toast.error('At least one item is required');
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  // Update item field
  const updateItem = (index, field, value) => {
    const newItems = [...items];
    if (field === 'item_name') {
      newItems[index][field] = toUpperCase(value);
    } else {
      newItems[index][field] = value;
    }
    setItems(newItems);
  };

  // Handle checkout
  const handleCheckout = async () => {
    if (!window.confirm('Are you sure you want to end your shift? You will need to check in again to create new entries.')) {
      return;
    }
    
    try {
      const deviceId = getDeviceId();
      const response = await axios.post(`${API_URL}/store/security-checkout`, {
        device_id: deviceId,
        reason: 'shift_end'
      });
      
      toast.success(response.data.message || 'Checked out successfully');
      
      // Clear local storage and redirect to check-in
      localStorage.removeItem('security_checkin');
      window.location.href = '/security-checkin';
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to check out');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_name_raw || !formData.nature_of_inward) {
      toast.error('Please fill all required fields');
      return;
    }
    
    // Validate items
    const validItems = items.filter(item => item.item_name && item.quantity);
    if (validItems.length === 0) {
      toast.error('Please add at least one item with name and quantity');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        unit_location_id: activeCheckin.unit_location_id,
        unit_location_name: activeCheckin.unit_location_name,
        supplier_name_raw: toUpperCase(formData.supplier_name_raw),
        invoice_dc_no: toUpperCase(formData.invoice_dc_no) || null,
        nature_of_inward: formData.nature_of_inward,
        vehicle_no: toUpperCase(formData.vehicle_no) || null,
        driver_name: toUpperCase(formData.driver_name) || null,
        driver_contact: formData.driver_contact || null,
        remarks: toUpperCase(formData.remarks) || null,
        items: validItems.map(item => ({
          item_name: item.item_name,
          quantity: parseFloat(item.quantity) || 0,
          uom: item.uom || 'NOS',
          num_packages: item.num_packages ? parseInt(item.num_packages) : null
        })),
        security_checkin_id: activeCheckin.id,
        security_person_name: activeCheckin.security_person_name,
        device_id: getDeviceId()
      };
      
      const response = await axios.post(`${API_URL}/store/gate-entry/public/v2`, payload);
      
      if (response.data.success) {
        setGateEntryNo(response.data.gate_entry_no);
        setSubmitted(true);
        toast.success('Gate Entry created successfully!');
      }
    } catch (error) {
      console.error('Error creating gate entry:', error);
      if (error.response?.status === 403) {
        // Session expired - redirect to check-in
        toast.error('Session expired. Please check-in again.');
        localStorage.removeItem('security_checkin');
        setTimeout(() => {
          window.location.href = '/security-checkin';
        }, 1500);
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to create gate entry');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_name_raw: '',
      invoice_dc_no: '',
      nature_of_inward: '',
      vehicle_no: '',
      driver_name: '',
      driver_contact: '',
      remarks: ''
    });
    setItems([{ item_name: '', quantity: '', uom: 'NOS', num_packages: '' }]);
    setSubmitted(false);
    setGateEntryNo('');
  };

  // Show loading while checking session
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-muted-foreground">Checking session...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Redirect to check-in if no valid session
  if (!activeCheckin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-amber-700">Check-In Required</h2>
              <p className="text-muted-foreground">
                You need to complete your daily check-in before accessing the gate entry form.
              </p>
              <Button 
                onClick={() => window.location.href = '/security-checkin'} 
                className="w-full"
              >
                <Shield className="w-5 h-5 mr-2" />
                Go to Check-In
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-700">Entry Recorded!</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Gate Entry Number</p>
                <p className="text-3xl font-bold text-blue-700">{gateEntryNo}</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Please note this number. Store department will use it for further processing.
              </p>
              <Button onClick={resetForm} className="w-full mt-4">
                Create Another Entry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-end mb-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.href = '/security-checkin'}
              data-testid="home-btn"
            >
              <Home className="w-4 h-4 mr-1" />
              Home
            </Button>
          </div>
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Truck className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Security Gate Entry</h1>
          <p className="text-muted-foreground">Material Inward Registration</p>
        </div>

        {/* Active Check-in Info with Actions */}
        <Card className="mb-4 border-green-200 bg-green-50/50">
          <CardContent className="py-3">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-green-600" />
                    <span className="font-medium">{activeCheckin.security_person_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-green-600" />
                    <span>{activeCheckin.unit_location_name}</span>
                  </div>
                </div>
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              {/* Session Actions */}
              <div className="flex items-center justify-between border-t border-green-200 pt-3">
                <span className="text-xs text-muted-foreground">
                  Checked in: {new Date(activeCheckin.checked_in_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.location.href = '/gate-entry/register'}
                    data-testid="view-register-btn"
                  >
                    <ClipboardList className="w-4 h-4 mr-1" />
                    View Register
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    onClick={handleCheckout}
                    data-testid="checkout-btn"
                  >
                    <Shield className="w-4 h-4 mr-1" />
                    End Shift
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              New Material Entry
            </CardTitle>
            <CardDescription>
              Record incoming material. Store will link to PO later.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Supplier Name */}
              <div className="space-y-2">
                <Label>Supplier / Party Name *</Label>
                <Input
                  placeholder="Enter supplier or party name"
                  value={formData.supplier_name_raw}
                  onChange={(e) => setFormData({ ...formData, supplier_name_raw: toUpperCase(e.target.value) })}
                  required
                  className="uppercase"
                />
              </div>

              {/* Invoice / DC No */}
              <div className="space-y-2">
                <Label>Invoice / DC Number</Label>
                <Input
                  placeholder="Enter invoice or DC number"
                  value={formData.invoice_dc_no}
                  onChange={(e) => setFormData({ ...formData, invoice_dc_no: toUpperCase(e.target.value) })}
                  className="uppercase"
                />
              </div>

              {/* Nature of Inward */}
              <div className="space-y-2">
                <Label>Nature of Inward *</Label>
                <Select 
                  value={formData.nature_of_inward} 
                  onValueChange={(value) => setFormData({ ...formData, nature_of_inward: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type of material" />
                  </SelectTrigger>
                  <SelectContent>
                    {NATURE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vehicle and Driver */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vehicle No.</Label>
                  <Input
                    placeholder="e.g., MH12AB1234"
                    value={formData.vehicle_no}
                    onChange={(e) => setFormData({ ...formData, vehicle_no: toUpperCase(e.target.value) })}
                    className="uppercase"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Driver Name</Label>
                  <Input
                    placeholder="Driver's name"
                    value={formData.driver_name}
                    onChange={(e) => setFormData({ ...formData, driver_name: toUpperCase(e.target.value) })}
                    className="uppercase"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Driver Contact</Label>
                <Input
                  placeholder="Driver's phone number"
                  value={formData.driver_contact}
                  onChange={(e) => setFormData({ ...formData, driver_contact: e.target.value })}
                />
              </div>

              {/* Items Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Items *</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Item Name *"
                          value={item.item_name}
                          onChange={(e) => updateItem(index, 'item_name', e.target.value)}
                          className="uppercase"
                          required
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            type="number"
                            placeholder="Qty *"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            min="0"
                            step="any"
                            required
                          />
                          <Select
                            value={item.uom || 'NOS'}
                            onValueChange={(val) => updateItem(index, 'uom', val)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="UOM" />
                            </SelectTrigger>
                            <SelectContent>
                              {UOM_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            placeholder="Packages"
                            value={item.num_packages}
                            onChange={(e) => updateItem(index, 'num_packages', e.target.value)}
                            min="0"
                          />
                        </div>
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Enter item names as they appear on delivery documents. Store will map to actual inventory items.
                </p>
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <Label>Remarks / Additional Notes</Label>
                <Textarea
                  placeholder="Any additional information"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: toUpperCase(e.target.value) })}
                  rows={2}
                  className="uppercase"
                />
              </div>

              {/* Submit Button */}
              <Button type="submit" className="w-full h-12 text-lg" disabled={loading}>
                {loading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Package className="w-5 h-5 mr-2" />
                    Submit Gate Entry
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          For assistance, contact Store Department
        </p>
      </div>
    </div>
  );
}
